import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import OfferForm from "../components/OfferForm";
import { useOffers } from "../hooks/useOffers";
import { useQueries } from "../../queries/hooks/useQueries";
import useOfferings from "../../offerings/hooks/useOfferings";

import Loader from "../../../components/common/Loader";
import ErrorMessage from "../../../components/common/ErrorMessage";
import "../styles/create-offer.css";


const CreateOfferPage = () => {
  const navigate = useNavigate();

  const {
    createOffer,
    saving,
    error: offerError,
  } = useOffers();

  const {
    queries = [],
    loading: queriesLoading,
    error: queriesError,
    fetchQueries,
  } = useQueries();

  const {
    offerings = [],
    loading: offeringsLoading,
    error: offeringsError,
    fetchOfferings,
  } = useOfferings();

  const [submitError, setSubmitError] = useState(null);

  /*
  |--------------------------------------------------------------------------
  | FETCH PAGE DATA
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    let mounted = true;

    const loadData = async () => {
      try {
        await Promise.all([
          fetchQueries(),
          fetchOfferings(),
        ]);
      } catch (error) {
        if (mounted) {
          console.error(
            "Failed to load create offer data:",
            error
          );
        }
      }
    };

    loadData();

    return () => {
      mounted = false;
    };
  }, [fetchQueries, fetchOfferings]);

  /*
  |--------------------------------------------------------------------------
  | CREATE OFFER
  |--------------------------------------------------------------------------
  */

  const handleSubmit = async (formData) => {
    setSubmitError(null);

    try {
      const created = await createOffer(formData);

      if (created?.id) {
        navigate(`/offers/${created.id}`, {
          replace: true,
        });

        return;
      }

      navigate("/offers");
    } catch (err) {
      setSubmitError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to create offer"
      );
    }
  };

  /*
  |--------------------------------------------------------------------------
  | NORMALIZE OFFERINGS
  |--------------------------------------------------------------------------
  |
  | Handles:
  |   id
  |   _id
  |   offeringId
  |   offering_id
  |
  */

  const normalizedOfferings = (
    Array.isArray(offerings)
      ? offerings
      : offerings?.data || []
  )
    .map((offering) => {
      const id =
        offering?.id ??
        offering?._id ??
        offering?.offeringId ??
        offering?.offering_id;

      return {
        id: id ? String(id) : "",
        name:
          offering?.name ||
          offering?.title ||
          offering?.code ||
          `Offering ${id || ""}`,
      };
    })
    .filter((offering) => offering.id);

  const loading =
    queriesLoading || offeringsLoading;

  /*
  |--------------------------------------------------------------------------
  | DEBUG
  |--------------------------------------------------------------------------
  */

  console.log(
    "CreateOfferPage offerings:",
    offerings
  );

  console.log(
    "CreateOfferPage normalized offerings:",
    normalizedOfferings
  );

  return (
    <div className="create-offer-page">
      <div className="page-header">
        <div>
          <h1>Create Offer</h1>

          <p>
            Create a draft offer for a client query.
          </p>
        </div>
      </div>

      {queriesError && (
        <ErrorMessage
          message={queriesError}
        />
      )}

      {offeringsError && (
        <ErrorMessage
          message={offeringsError}
        />
      )}

      {offerError && (
        <ErrorMessage
          message={
            offerError?.response?.data?.message ||
            offerError?.message ||
            "Unable to load offers"
          }
        />
      )}

      {submitError && (
        <ErrorMessage
          message={submitError}
        />
      )}

      {loading ? (
        <Loader />
      ) : (
        <OfferForm
          queries={queries}
          offerings={normalizedOfferings}
          onSubmit={handleSubmit}
          loading={saving}
          onCancel={() =>
            navigate("/offers")
          }
        />
      )}
    </div>
  );
};

export default CreateOfferPage;
